import {createReducer, on} from '@ngrx/store';
import {
  ApplicationAppEntityApplicationRead,
  EntityTypeAppEntityDictionaryEntityType,
  RoadOccupationPurposeAppEntityDictionaryRoadOccupationPurpose,
  RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee,
  RoadOccupationTypeAppEntityDictionaryRoadOccupationType,
  ShipmentAppEntityShipmentRead
} from '../../openapi';
import {
  approveApplication,
  approveApplicationFailure,
  approveApplicationSuccess,
  createApplication,
  createApplicationFailure,
  createApplicationSuccess,
  fakeSignApplication,
  fakeSignApplicationFailure,
  fakeSignApplicationSuccess,
  loadApplication,
  loadApplicationFailure,
  loadApplicationList,
  loadApplicationListFailure,
  loadApplicationListSuccess,
  loadApplicationSuccess,
  loadEntityTypes,
  loadEntityTypesFailure,
  loadEntityTypesSuccess,
  loadPayments,
  loadPaymentsFailure,
  loadPaymentsSuccess,
  loadPurposeFeeCollection,
  loadPurposeFeeCollectionFailure,
  loadPurposeFeeCollectionSuccess,
  loadRoadOccupationTypes,
  loadRoadOccupationTypesFailure,
  loadRoadOccupationTypesSuccess,
  loadServiceAttachmentPositions,
  loadServiceAttachmentPositionsFailure,
  loadServiceAttachmentPositionsSuccess,
  loadShipment,
  loadShipmentFailure,
  loadShipments,
  loadShipmentsFailure,
  loadShipmentsSuccess,
  loadShipmentSuccess,
  sendApplication,
  sendApplicationFailure,
  sendApplicationSuccess,
  setApplication,
  setPayments,
  setShipments,
  signApplication,
  signApplicationFailure,
  signApplicationSuccess,
  updateAdPlacementOccupations,
  updateAdPlacementOccupationsFailure,
  updateAdPlacementOccupationsSuccess,
  updateApplicants,
  updateApplicantsFailure,
  updateApplicantsSuccess,
  updateApplication,
  updateApplicationAttachments,
  updateApplicationAttachmentsFailure,
  updateApplicationAttachmentsSuccess,
  updateApplicationFailure,
  updateApplicationSuccess,
  updateContractors,
  updateContractorsFailure,
  updateContractorsSuccess,
  updateLocalisationData,
  updateLocalisationDataFailure,
  updateLocalisationDataSuccess,
  updateLocationOfDevicesUnrelatedToRoadOccupations,
  updateLocationOfDevicesUnrelatedToRoadOccupationsFailure,
  updateLocationOfDevicesUnrelatedToRoadOccupationsSuccess,
  updateOccupationOfLanePlacementConstructionObjects,
  updateOccupationOfLanePlacementConstructionObjectsFailure,
  updateOccupationOfLanePlacementConstructionObjectsSuccess,
  updateOccupations,
  updateOccupationsFailure,
  updateOccupationsSuccess,
  updateParcels,
  updateParcelsFailure,
  updateParcelsSuccess,
  updateVehicles,
  updateVehiclesFailure,
  updateVehiclesSuccess
} from '../actions/application.actions';
import {ApplicationListItem, ApplicationPayment, AttachmentPositions} from '../../services/application.common.model';

export interface ApplicationState {
  loading: number;
  pending: number;
  application: ApplicationAppEntityApplicationRead | null;
  applicationPayment: ApplicationPayment | null;
  shipments: ShipmentAppEntityShipmentRead[];
  shipment: ShipmentAppEntityShipmentRead | null;
  applicationList: ApplicationListItem[];
  roadOccupationTypes: RoadOccupationTypeAppEntityDictionaryRoadOccupationType[];
  entityTypes: EntityTypeAppEntityDictionaryEntityType[];
  serviceAttachmentPositions: AttachmentPositions;
  occupationPurposeCollection: RoadOccupationPurposeAppEntityDictionaryRoadOccupationPurpose[];
  purposeFeeCollection: RoadOccupationPurposeFeeAppEntityDictionaryRoadOccupationPurposeFee[];
}

export const initialApplicationState: ApplicationState = {
  pending: 0,
  loading: 0,
  application: null,
  applicationPayment: null,
  shipments: [],
  shipment: null,
  applicationList: [],
  roadOccupationTypes: [],
  entityTypes: [],
  serviceAttachmentPositions: [],
  occupationPurposeCollection: [],
  purposeFeeCollection: []
};

export const applicationReducer = createReducer(
  initialApplicationState,
  on(createApplication, state => ({...state, pending: state.pending + 1})),
  on(createApplicationSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(createApplicationFailure, state => ({...state, pending: state.pending - 1})),

  on(setApplication, (state, action) => ({...state, application: action.application})),

  on(loadApplication, state => ({...state, loading: state.loading + 1})),
  on(loadApplicationSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    application: action.application
  })),
  on(loadApplicationFailure, state => ({...state, loading: state.loading - 1})),

  on(updateApplication, state => ({...state, pending: state.pending + 1})),
  on(updateApplicationSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateApplicationFailure, state => ({...state, pending: state.pending - 1})),

  on(updateApplicants, state => ({...state, pending: state.pending + 1})),
  on(updateApplicantsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateApplicantsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateContractors, state => ({...state, pending: state.pending + 1})),
  on(updateContractorsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateContractorsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateOccupations, state => ({...state, pending: state.pending + 1})),
  on(updateOccupationsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateOccupationsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateOccupationOfLanePlacementConstructionObjects, state => ({...state, pending: state.pending + 1})),
  on(updateOccupationOfLanePlacementConstructionObjectsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateOccupationOfLanePlacementConstructionObjectsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateAdPlacementOccupations, state => ({...state, pending: state.pending + 1})),
  on(updateAdPlacementOccupationsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateAdPlacementOccupationsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateLocationOfDevicesUnrelatedToRoadOccupations, state => ({...state, pending: state.pending + 1})),
  on(updateLocationOfDevicesUnrelatedToRoadOccupationsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateLocationOfDevicesUnrelatedToRoadOccupationsFailure, (state, action) => ({
    ...state,
    pending: state.pending - 1
  })),

  on(updateApplicationAttachments, state => ({...state, pending: state.pending + 1})),
  on(updateApplicationAttachmentsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateApplicationAttachmentsFailure, state => ({...state, pending: state.pending - 1})),

  on(approveApplication, state => ({...state, pending: state.pending + 1})),
  on(approveApplicationSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(approveApplicationFailure, state => ({...state, pending: state.pending - 1})),

  on(loadApplicationList, state => ({...state, loading: state.loading + 1})),
  on(loadApplicationListSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    applicationList: action.applicationList
  })),
  on(loadApplicationListFailure, (state) => ({...state, loading: state.loading - 1})),

  on(loadRoadOccupationTypes, state => ({...state, loading: state.loading + 1})),
  on(loadRoadOccupationTypesSuccess, (state, action) => ({
      ...state,
      roadOccupationTypes: action.roadOccupationTypes,
      loading: state.loading - 1
    })
  ),
  on(loadRoadOccupationTypesFailure, state => ({...state, loading: state.loading - 1})),

  on(setPayments, (state, action) => ({...state, applicationPayment: action.applicationPayment})),

  on(loadPayments, state => ({...state, loading: state.loading + 1})),
  on(loadPaymentsSuccess, (state, action) => ({
      ...state,
      applicationPayment: action.applicationPayment,
      loading: state.loading - 1
    })
  ),
  on(loadPaymentsFailure, state => ({...state, loading: state.loading - 1})),

  on(sendApplication, state => ({...state, pending: state.pending + 1})),
  on(sendApplicationSuccess, state => ({...state, pending: state.pending - 1})),
  on(sendApplicationFailure, state => ({...state, pending: state.pending - 1})),

  on(signApplication, state => ({...state, pending: state.pending + 1})),
  on(signApplicationSuccess, state => ({...state, pending: state.pending - 1})),
  on(signApplicationFailure, state => ({...state, pending: state.pending - 1})),

  on(setShipments, (state, action) => ({...state, shipments: action.shipments})),

  on(loadShipments, state => ({...state, loading: state.loading + 1})),
  on(loadShipmentsSuccess, (state, action) => ({
    ...state,
    shipments: action.shipments,
    loading: state.loading - 1
  })),
  on(loadShipmentsFailure, state => ({...state, loading: state.loading - 1})),

  on(loadShipment, state => ({...state, loading: state.loading + 1})),
  on(loadShipmentSuccess, (state, action) => ({
      ...state,
      shipment: action.shipment,
      loading: state.loading - 1
    })
  ),
  on(loadShipmentFailure, state => ({...state, loading: state.loading - 1})),

  on(updateParcels, state => ({...state, pending: state.pending + 1})),
  on(updateParcelsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateParcelsFailure, state => ({...state, pending: state.pending - 1})),

  on(updateVehicles, state => ({...state, pending: state.pending + 1})),
  on(updateVehiclesSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateVehiclesFailure, state => ({...state, pending: state.pending - 1})),

  on(updateLocalisationData, state => ({...state, pending: state.pending + 1})),
  on(updateLocalisationDataSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateLocalisationDataFailure, state => ({...state, pending: state.pending - 1})),

  on(fakeSignApplication, state => ({...state, pending: state.pending + 1})),
  on(fakeSignApplicationSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1
  })),
  on(fakeSignApplicationFailure, state => ({...state, pending: state.pending - 1})),
  on(loadEntityTypes, state => ({...state, loading: state.loading + 1})),
  on(loadEntityTypesSuccess, (state, action) => ({
    ...state,
    entityTypes: action.entityTypes,
    loading: state.loading - 1
  })),
  on(loadEntityTypesFailure, state => ({...state, loading: state.loading - 1})),
  on(loadServiceAttachmentPositions, state => ({...state, loading: state.loading + 1})),
  on(loadServiceAttachmentPositionsSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    serviceAttachmentPositions: action.serviceAttachmentPositions
  })),
  on(loadServiceAttachmentPositionsFailure, state => ({...state, loading: state.loading - 1})),

  on(loadPurposeFeeCollection, state => ({...state, loading: state.loading + 1})),
  on(loadPurposeFeeCollectionSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    purposeFeeCollection: action.purposeFeeCollection
  })),
  on(loadPurposeFeeCollectionFailure, state => ({...state, loading: state.loading - 1}))
);
